import React, { useState } from 'react';
import Icon from './Icon';

type BtnVariant = 'primary' | 'danger' | 'ghost' | 'subtle';

interface BtnProps {
  children?: React.ReactNode;
  variant?: BtnVariant;
  onClick?: () => void;
  icon?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const Btn: React.FC<BtnProps> = ({
  children,
  variant = 'ghost',
  onClick,
  icon,
  style = {},
  disabled,
  type = 'button',
}) => {
  const [hov, setHov] = useState(false);
  const base: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '9px 16px',
    borderRadius: 'var(--r-sm)',
    fontFamily: 'var(--sans)',
    fontSize: 14,
    fontWeight: 500,
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    opacity: disabled ? 0.4 : 1,
  };
  const variants: Record<BtnVariant, React.CSSProperties> = {
    primary: {
      background: 'var(--teal-solid)',
      color: 'rgba(0,0,0,0.85)',
      boxShadow: '0 4px 16px var(--teal-mid)',
    },
    danger: {
      background: 'var(--red-dim)',
      color: 'var(--red)',
      border: '1px solid var(--red-mid)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    },
    ghost: {
      background: hov ? 'var(--glass-bg-hover)' : 'var(--glass-bg)',
      color: 'var(--text1)',
      border: '1px solid var(--glass-border)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    },
    subtle: {
      background: hov ? 'var(--glass-bg-hover)' : 'var(--glass-bg)',
      color: 'var(--text0)',
      border: '1px solid var(--glass-border-strong)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    },
  };

  return (
    <button
      type={type}
      style={{ ...base, ...variants[variant], ...style }}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {icon && <Icon name={icon} size={15} color="currentColor" />}
      {children}
    </button>
  );
};

export default Btn;
